import { type FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  makeAuctionsAllChannel,
  makeAuctionUserNotificationChannel,
  RegistrationAPIEvents,
} from '@furality/centrifugo-types';
import { useAuth } from '@furality/frontend-stack';
import { parseAuctionEnvelope } from '@/lib/auctions/centrifugoEvents.ts';
import useChannelSubscription from '@/lib/hooks/useChannelSubscription.ts';
import useFlag from '@/lib/hooks/useFlag.ts';
import useToasts from '@/lib/useToast.ts';

const AuctionGlobalNotifications: FC = () => {
  const { pushAuctionToast, toasts } = useToasts();
  const { t } = useTranslation('auctions');

  const activatedToastIdRef = useRef<string | null>(null);

  useChannelSubscription(makeAuctionsAllChannel(), (raw) => {
    const envelope = parseAuctionEnvelope(raw);

    if (envelope?.type !== RegistrationAPIEvents.Activated) {
      return;
    }

    const isActivatedToastVisible =
      activatedToastIdRef.current !== null &&
      toasts.some((toast) => toast.id === activatedToastIdRef.current);

    if (isActivatedToastVisible) {
      return;
    }

    activatedToastIdRef.current = pushAuctionToast({
      auctionId: envelope.data.auctionId,
      title: t('notifications.activated.title'),
      message: t('notifications.activated.message'),
      options: { linkToList: true },
    });
  });

  return null;
};

type AuctionUserNotificationsProps = {
  userId: string;
};

const AuctionUserNotifications: FC<AuctionUserNotificationsProps> = ({ userId }) => {
  const { pushAuctionToast } = useToasts();
  const { t } = useTranslation('auctions');

  useChannelSubscription(makeAuctionUserNotificationChannel(userId), (raw) => {
    const envelope = parseAuctionEnvelope(raw);

    if (!envelope) {
      return;
    }

    switch (envelope.type) {
      case RegistrationAPIEvents.Outbid: {
        pushAuctionToast({
          auctionId: envelope.data.auctionId,
          title: t('notifications.outbid.title'),
          message: t('notifications.outbid.message'),
        });

        break;
      }

      case RegistrationAPIEvents.Won: {
        pushAuctionToast({
          auctionId: envelope.data.auctionId,
          title: t('notifications.won.title'),
          message: t('notifications.won.message'),
        });

        break;
      }

      default:
        break;
    }
  });

  return null;
};

const AuctionNotificationProvider: FC = () => {
  const auth = useAuth();
  const auctionsEnabled = useFlag('auctions');

  if (!auctionsEnabled) {
    return null;
  }

  return (
    <>
      <AuctionGlobalNotifications />

      {auth.state === 'loggedIn' && auth.user.id ? (
        <AuctionUserNotifications userId={auth.user.id} />
      ) : null}
    </>
  );
};

export default AuctionNotificationProvider;
